import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, User, Search, Check, X, Upload, Eye, Phone, FileText } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DriversPage = () => {
  const { user } = useAuth();
  const [drivers, setDrivers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterVendor, setFilterVendor] = useState("all");
  const [uploadingImage, setUploadingImage] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    license_number: "",
    license_image_url: "",
    license_expiry: "",
    vendor_id: "",
    vendor_name: "",
    address: "",
    emergency_contact: "",
    date_of_birth: "",
    aadhar_number: "",
    aadhar_image_url: "",
    photo_url: "",
    status: "active"
  });

  const canVerify = user?.role === "admin" || user?.role === "super_admin";
  const canDelete = user?.role === "admin" || user?.role === "super_admin";

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [driversRes, vendorsRes] = await Promise.all([
        axios.get(`${API_URL}/drivers`),
        axios.get(`${API_URL}/vendors`)
      ]);
      setDrivers(driversRes.data);
      setVendors(vendorsRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedDriver) {
        await axios.put(`${API_URL}/drivers/${selectedDriver.id}`, formData);
        toast.success("Driver updated successfully");
      } else {
        await axios.post(`${API_URL}/drivers`, formData);
        toast.success("Driver added successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleEdit = (driver) => {
    setSelectedDriver(driver);
    setFormData({
      name: driver.name,
      mobile: driver.mobile,
      license_number: driver.license_number || "",
      license_image_url: driver.license_image_url || "",
      license_expiry: driver.license_expiry || "",
      vendor_id: driver.vendor_id || "",
      vendor_name: driver.vendor_name || "",
      address: driver.address || "",
      emergency_contact: driver.emergency_contact || "",
      date_of_birth: driver.date_of_birth || "",
      aadhar_number: driver.aadhar_number || "",
      aadhar_image_url: driver.aadhar_image_url || "",
      photo_url: driver.photo_url || "",
      status: driver.status || "active"
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/drivers/${selectedDriver.id}`);
      toast.success("Driver deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedDriver(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete driver");
    }
  };

  const handleVerify = async (driverId) => {
    try {
      await axios.post(`${API_URL}/drivers/${driverId}/verify`);
      toast.success("Driver verified successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to verify driver");
    }
  };

  const resetForm = () => {
    setSelectedDriver(null);
    setFormData({
      name: "",
      mobile: "",
      license_number: "",
      license_image_url: "",
      license_expiry: "",
      vendor_id: "",
      vendor_name: "",
      address: "",
      emergency_contact: "",
      date_of_birth: "",
      aadhar_number: "",
      aadhar_image_url: "",
      photo_url: "",
      status: "active"
    });
  };

  const handleImageUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingImage(fieldName);
    const uploadFormData = new FormData();
    uploadFormData.append("file", file);
    
    try {
      const response = await axios.post(`${API_URL}/upload-image`, uploadFormData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setFormData({ ...formData, [fieldName]: response.data.url });
      toast.success("Image uploaded successfully");
    } catch (error) {
      toast.error("Failed to upload image");
    } finally {
      setUploadingImage(null);
    }
  };

  const handleVendorChange = (vendorId) => {
    const vendor = vendors.find(v => v.id === vendorId);
    setFormData({
      ...formData,
      vendor_id: vendorId,
      vendor_name: vendor?.name || ""
    });
  };

  const filteredDrivers = drivers.filter(driver => {
    const matchesSearch = driver.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         driver.mobile.includes(searchTerm) ||
                         (driver.license_number && driver.license_number.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesVendor = filterVendor === "all" || driver.vendor_id === filterVendor;
    return matchesSearch && matchesVendor;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case "active": return "bg-green-500/20 text-green-400 border-green-500/50";
      case "inactive": return "bg-red-500/20 text-red-400 border-red-500/50";
      default: return "bg-gray-500/20 text-gray-400 border-gray-500/50";
    }
  };

  const getVerificationBadge = (status) => {
    switch (status) {
      case "verified": return "bg-green-500/20 text-green-400 border-green-500/50";
      case "rejected": return "bg-red-500/20 text-red-400 border-red-500/50";
      default: return "bg-yellow-500/20 text-yellow-400 border-yellow-500/50";
    }
  };

  return (
    <div className="space-y-6" data-testid="drivers-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold">Driver Master</h1>
          <p className="text-muted-foreground">Manage all drivers across vendors</p>
        </div>
        <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-primary" data-testid="add-driver-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Driver
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, mobile, or license..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 bg-secondary/50"
            data-testid="driver-search"
          />
        </div>
        <Select value={filterVendor} onValueChange={setFilterVendor}>
          <SelectTrigger className="w-full sm:w-[200px] bg-secondary/50" data-testid="vendor-filter">
            <SelectValue placeholder="Filter by vendor" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Vendors</SelectItem>
            {vendors.map((vendor) => (
              <SelectItem key={vendor.id} value={vendor.id}>{vendor.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-primary">{drivers.length}</div>
            <p className="text-sm text-muted-foreground">Total Drivers</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-400">{drivers.filter(d => d.status === "active").length}</div>
            <p className="text-sm text-muted-foreground">Active</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-yellow-400">{drivers.filter(d => d.verification_status === "pending").length}</div>
            <p className="text-sm text-muted-foreground">Pending Verification</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-400">{drivers.filter(d => d.license_image_url).length}</div>
            <p className="text-sm text-muted-foreground">With License Image</p>
          </CardContent>
        </Card>
      </div>

      {/* Drivers Table */}
      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground">Driver</TableHead>
                <TableHead className="text-muted-foreground">Contact</TableHead>
                <TableHead className="text-muted-foreground">License</TableHead>
                <TableHead className="text-muted-foreground">Vendor</TableHead>
                <TableHead className="text-muted-foreground">Status</TableHead>
                <TableHead className="text-muted-foreground">Verification</TableHead>
                <TableHead className="text-muted-foreground text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">Loading...</TableCell>
                </TableRow>
              ) : filteredDrivers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No drivers found
                  </TableCell>
                </TableRow>
              ) : (
                filteredDrivers.map((driver) => (
                  <TableRow key={driver.id} className="border-border" data-testid={`driver-row-${driver.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                          {driver.photo_url ? (
                            <img src={driver.photo_url} alt={driver.name} className="w-10 h-10 rounded-full object-cover" />
                          ) : (
                            <User className="w-5 h-5 text-primary" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{driver.name}</p>
                          {driver.date_of_birth && (
                            <p className="text-xs text-muted-foreground">DOB: {driver.date_of_birth}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <Phone className="w-3 h-3" />
                        {driver.mobile}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <p className="font-mono text-sm">{driver.license_number || "N/A"}</p>
                        {driver.license_image_url && (
                          <a href={driver.license_image_url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1">
                            <Eye className="w-3 h-3" /> View License
                          </a>
                        )}
                        {driver.license_expiry && (
                          <p className="text-xs text-muted-foreground">Exp: {driver.license_expiry}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{driver.vendor_name || "—"}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={getStatusBadge(driver.status)}>
                        {driver.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={getVerificationBadge(driver.verification_status)}>
                        {driver.verification_status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {canVerify && driver.verification_status === "pending" && (
                          <Button variant="ghost" size="icon" onClick={() => handleVerify(driver.id)} className="h-8 w-8 text-green-400 hover:text-green-300" data-testid={`verify-driver-${driver.id}`}>
                            <Check className="w-4 h-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(driver)} className="h-8 w-8" data-testid={`edit-driver-${driver.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {canDelete && (
                          <Button variant="ghost" size="icon" onClick={() => { setSelectedDriver(driver); setDeleteDialogOpen(true); }} className="h-8 w-8 text-destructive hover:text-destructive" data-testid={`delete-driver-${driver.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedDriver ? "Edit Driver" : "Add New Driver"}</DialogTitle>
            <DialogDescription>
              {selectedDriver ? "Update driver information" : "Enter driver details"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-secondary/50"
                  required
                  data-testid="driver-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mobile">Mobile Number *</Label>
                <Input
                  id="mobile"
                  value={formData.mobile}
                  onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                  className="bg-secondary/50"
                  required
                  data-testid="driver-mobile-input"
                />
              </div>
            </div>

            {/* License Info */}
            <div className="p-3 bg-secondary/30 rounded-lg space-y-3">
              <h4 className="font-medium text-sm flex items-center gap-2">
                <FileText className="w-4 h-4" /> License Details
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="license_number">License Number</Label>
                  <Input
                    id="license_number"
                    value={formData.license_number}
                    onChange={(e) => setFormData({ ...formData, license_number: e.target.value.toUpperCase() })}
                    className="bg-secondary/50"
                    data-testid="driver-license-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="license_expiry">License Expiry</Label>
                  <Input
                    id="license_expiry"
                    type="date"
                    value={formData.license_expiry}
                    onChange={(e) => setFormData({ ...formData, license_expiry: e.target.value })}
                    className="bg-secondary/50"
                    data-testid="driver-license-expiry"
                  />
                </div>
              </div>
              {/* License Image Upload */}
              <div className="space-y-2">
                <Label>License Image</Label>
                <div className="flex items-center gap-3">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, "license_image_url")}
                    className="hidden"
                    id="license_image_upload"
                    data-testid="driver-license-image-input"
                  />
                  <label
                    htmlFor="license_image_upload"
                    className={`flex items-center gap-2 px-4 py-2 bg-secondary/50 border border-border rounded-md cursor-pointer hover:bg-secondary/70 text-sm ${uploadingImage === "license_image_url" ? "opacity-50" : ""}`}
                  >
                    <Upload className="w-4 h-4" />
                    {formData.license_image_url ? "Change License Image" : "Upload License Image"}
                  </label>
                  {formData.license_image_url && (
                    <a href={formData.license_image_url} target="_blank" rel="noreferrer" className="text-primary text-sm hover:underline flex items-center gap-1">
                      <Eye className="w-4 h-4" /> View
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* Vendor */}
            <div className="space-y-2">
              <Label>Associated Vendor</Label>
              <Select value={formData.vendor_id || "none"} onValueChange={(val) => handleVendorChange(val === "none" ? "" : val)}>
                <SelectTrigger className="bg-secondary/50" data-testid="driver-vendor-select">
                  <SelectValue placeholder="Select vendor (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Vendor</SelectItem>
                  {vendors.map((vendor) => (
                    <SelectItem key={vendor.id} value={vendor.id}>{vendor.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Additional Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date_of_birth">Date of Birth</Label>
                <Input
                  id="date_of_birth"
                  type="date"
                  value={formData.date_of_birth}
                  onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                  className="bg-secondary/50"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="emergency_contact">Emergency Contact</Label>
                <Input
                  id="emergency_contact"
                  value={formData.emergency_contact}
                  onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })}
                  className="bg-secondary/50"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="bg-secondary/50"
              />
            </div>

            {/* Aadhar Info */}
            <div className="p-3 bg-secondary/30 rounded-lg space-y-3">
              <h4 className="font-medium text-sm">Aadhar Details <span className="text-muted-foreground text-xs">(Optional)</span></h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="aadhar_number">Aadhar Number</Label>
                  <Input
                    id="aadhar_number"
                    value={formData.aadhar_number}
                    onChange={(e) => setFormData({ ...formData, aadhar_number: e.target.value })}
                    className="bg-secondary/50"
                    placeholder="XXXX XXXX XXXX"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Aadhar Image</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, "aadhar_image_url")}
                      className="hidden"
                      id="aadhar_image_upload"
                    />
                    <label
                      htmlFor="aadhar_image_upload"
                      className={`flex items-center gap-2 px-3 py-2 bg-secondary/50 border border-border rounded-md cursor-pointer hover:bg-secondary/70 text-sm ${uploadingImage === "aadhar_image_url" ? "opacity-50" : ""}`}
                    >
                      <Upload className="w-4 h-4" />
                      {formData.aadhar_image_url ? "Change" : "Upload"}
                    </label>
                    {formData.aadhar_image_url && (
                      <a href={formData.aadhar_image_url} target="_blank" rel="noreferrer" className="text-primary text-xs hover:underline">View</a>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Photo & Status */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Driver Photo</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, "photo_url")}
                    className="hidden"
                    id="photo_upload"
                  />
                  <label
                    htmlFor="photo_upload"
                    className={`flex items-center gap-2 px-3 py-2 bg-secondary/50 border border-border rounded-md cursor-pointer hover:bg-secondary/70 text-sm ${uploadingImage === "photo_url" ? "opacity-50" : ""}`}
                  >
                    <Upload className="w-4 h-4" />
                    {formData.photo_url ? "Change Photo" : "Upload Photo"}
                  </label>
                  {formData.photo_url && (
                    <a href={formData.photo_url} target="_blank" rel="noreferrer" className="text-primary text-xs hover:underline">View</a>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger className="bg-secondary/50" data-testid="driver-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-primary" data-testid="save-driver-btn">
                {selectedDriver ? "Update Driver" : "Add Driver"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Driver</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedDriver?.name}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-driver">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DriversPage;
